// src/oura/components/aeo/AnswerMarkdown.tsx
//
// The engine's answer, rendered rather than printed raw (#3564 follow-up).
//
// ChatGPT answers in Markdown, and the panel was showing it verbatim: readers
// got `**Halina Dental Clinic**` and `([klinikgigi.my](https://…))` instead of
// bold text and a link. On an answer whose whole value is a ranked list of
// competitors, the list markers were the part being lost.
//
// Clamping is VISUAL, not textual. Cutting the string at 700 characters and
// then rendering it is how you get an unterminated `**` swallowing the rest of
// the paragraph: the truncation would decide the formatting. The full document
// is rendered and the box is given a height instead, so what is hidden is only
// hidden.
//
// No raw HTML: react-markdown does not pass it through by default, and this
// text comes from a third-party model, so that default is exactly right and is
// left alone.
//
// TABLES NEED `remark-gfm`. react-markdown alone implements CommonMark, which
// has no tables, so a Gemini answer that replied with one printed as its own
// pipes: `| Goal | Recommended Clinic | Location | | :--- | :--- |` and so on,
// which is the answer's most structured content arriving as its least readable
// (#4025 FE-23). GFM also brings strikethrough and task lists, which cost
// nothing and were being printed raw for the same reason.
//
// It does NOT loosen the HTML rule: `remark-gfm` adds Markdown syntax, not raw
// HTML passthrough, so a model that emits `<script>` is still rendered as text.

import { Children, createContext, useContext, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "../../lib/cn";
import { cleanAnswerText } from "../../lib/answerText";
import { useOuraRole } from "../../role";
import { MERCHANT_V2 } from "../../merchantV2";

/** Names to mark in the answer: the business in blue, the rivals in amber. */
export interface AnswerHighlight {
    you: string[];
    rivals: string[];
    /** Rivals underlined rather than filled, for the question drawer's reading view. */
    soft?: boolean;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Marks every name inside the text nodes of `children`, leaving elements
 * (links, code) as they are. Matched on the whole name, case-insensitively,
 * longest first, so "Baba's Kitchen Express" is not marked as "Baba's Kitchen".
 */
function markNames(children: ReactNode, highlight: AnswerHighlight | undefined): ReactNode {
    if (!highlight) return children;
    const names = [...highlight.you, ...highlight.rivals].filter((n) => n.trim().length > 1);
    if (names.length === 0) return children;
    const you = new Set(highlight.you.map((n) => n.toLowerCase()));
    // Whole names only: a short name ("Inn", "Art") must not light up inside
    // "winning" or "part" and claim a mention the answer never made. The
    // lookarounds do not capture, so split() still returns the names at odd
    // indexes.
    const alternation = Array.from(new Set(names)).sort((a, b) => b.length - a.length).map(escapeRe).join("|");
    const pattern = new RegExp(`(?<![\\p{L}\\p{N}])(${alternation})(?![\\p{L}\\p{N}])`, "giu");
    return Children.map(children, (child) => {
        if (typeof child !== "string") return child;
        return child.split(pattern).map((part, i) =>
            i % 2 === 1 ? (
                <mark
                    key={i}
                    className={
                        you.has(part.toLowerCase())
                            ? "rounded bg-blue-50 px-[3px] font-semibold text-blue-700"
                            : highlight.soft
                                ? "bg-transparent font-medium text-gray-900 underline decoration-amber-300 decoration-2 underline-offset-[3px]"
                                : "rounded bg-amber-50 px-[3px] font-medium text-amber-800"
                    }
                >
                    {part}
                </mark>
            ) : (
                part
            ),
        );
    });
}

/**
 * The names in force, read by the static element overrides below. A context
 * rather than components built per render: react-markdown would remount every
 * paragraph each time a new component identity arrived.
 */
const HighlightContext = createContext<AnswerHighlight | undefined>(undefined);

function MarkedP({ children }: { children?: ReactNode }) {
    return <p>{markNames(children, useContext(HighlightContext))}</p>;
}
function MarkedLi({ children }: { children?: ReactNode }) {
    return <li>{markNames(children, useContext(HighlightContext))}</li>;
}
function MarkedStrong({ children }: { children?: ReactNode }) {
    return <strong>{markNames(children, useContext(HighlightContext))}</strong>;
}
function MarkedEm({ children }: { children?: ReactNode }) {
    return <em>{markNames(children, useContext(HighlightContext))}</em>;
}
function MarkedTh({ children }: { children?: ReactNode }) {
    return <th className="px-2 py-1.5 align-top font-semibold">{markNames(children, useContext(HighlightContext))}</th>;
}
function MarkedTd({ children }: { children?: ReactNode }) {
    return <td className="px-2 py-1.5 align-top">{markNames(children, useContext(HighlightContext))}</td>;
}

/**
 * A GFM table inside an answer.
 *
 * It scrolls SIDEWAYS rather than widening the panel: three columns of prose
 * in a drawer would otherwise squash to a word each. The wrapper scrolls, so
 * the rest of the answer keeps its own width, and `min-w` is what makes the
 * scroll happen instead of the squash.
 */
function AnswerTable({ children }: { children?: ReactNode }) {
    return (
        <div className="my-2 -mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-[22rem] border-collapse text-left text-[13px]">{children}</table>
        </div>
    );
}
function AnswerThead({ children }: { children?: ReactNode }) {
    return <thead className="bg-gray-50 text-gray-900">{children}</thead>;
}
function AnswerTr({ children }: { children?: ReactNode }) {
    return <tr className="border-b border-gray-100 last:border-b-0">{children}</tr>;
}

/**
 * A heading inside an answer, as a bold line (#3800). Unstyled, an `h2`
 * inherited the browser default, a 24px title inside a 14px evidence panel.
 * 28 of 555 stored API answers open with one.
 */
function AnswerHeading({ children }: { children?: ReactNode }) {
    return <p className="font-semibold text-gray-900">{markNames(children, useContext(HighlightContext))}</p>;
}

/** Roughly ten lines: enough to judge an answer, short enough to scan a page
 *  of them. Height rather than character count, because the clamp is visual. */
const CLAMP_CLASS = "max-h-52 overflow-hidden";
/** The same ten lines or so at reading size. */
const READING_CLAMP_CLASS = "max-h-72 overflow-hidden";

const EMPTY_ANSWER = "This engine returned an empty answer.";

export function AnswerMarkdown({
    text,
    className,
    highlight,
    emptyText = EMPTY_ANSWER,
    reading = false,
}: {
    text: string;
    /** 15px reading type with room between paragraphs: the drawer's answer, the thing it is opened to read. */
    reading?: boolean;
    className?: string;
    highlight?: AnswerHighlight;
    /** What an empty answer says. Google's AI has its own: showing no AI Overview is not a failure (#4180). */
    emptyText?: string;
}) {
    const [toggled, setExpanded] = useState(false);
    // The redesign shows a merchant the whole answer, no "Show full answer":
    // it is the thing they opened the drawer to read. Staff keep the clamp.
    const role = useOuraRole();
    const expanded = toggled || (MERCHANT_V2 && role !== "oura-staff");
    const clampable = !(MERCHANT_V2 && role !== "oura-staff");
    // Cleaned at render so it covers every answer already stored: the Maps
    // carousel off the front when (and only when) it is there, and paragraphs
    // back into a text that was captured with single newlines. See answerText.ts.
    const body = cleanAnswerText(text ?? "");

    // The fade and "Show full answer" only when the clamp actually cuts
    // something: over a one-line answer they made it read as disabled.
    // Measured, not guessed from the length, because the width decides.
    const clipRef = useRef<HTMLDivElement>(null);
    const [overflows, setOverflows] = useState(false);
    useLayoutEffect(() => {
        const el = clipRef.current;
        // Expanded, the box is not clamped and cannot be measured against the
        // clamp; the last reading stands, so "Show less" stays.
        if (!el || expanded) return;
        const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        if (el.firstElementChild) observer.observe(el.firstElementChild);
        return () => observer.disconnect();
    }, [expanded, body]);

    if (!body) {
        return <p className="my-0 text-sm italic text-gray-400">{emptyText}</p>;
    }

    return (
        <HighlightContext.Provider value={highlight}>
        <div className={className}>
            <div ref={clipRef} className={cn("relative", !expanded && (reading ? READING_CLAMP_CLASS : CLAMP_CLASS))}>
                {/* `prose` is not available here, so the elements are styled
                    directly, and the list padding is explicit because
                    index.css forces `list-style: revert !important` with
                    `padding-left: 0`, which drops markers outside the content
                    box wherever a list is not given room for them. */}
                <div className={cn(reading ? "space-y-3 text-[15px] leading-[1.65] text-gray-800" : "space-y-2 text-sm leading-relaxed text-gray-700", "[&_a]:break-all [&_a]:text-blue-600 [&_a:hover]:underline [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_em]:italic [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:pl-5 [&_p]:my-0 [&_strong]:font-semibold [&_strong]:text-gray-900 [&_ul]:my-1 [&_ul]:pl-5")}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            table: AnswerTable,
                            thead: AnswerThead,
                            tr: AnswerTr,
                            // Marked inside cells too, so the business and its
                            // rivals stay findable in a table exactly as they
                            // are in a paragraph.
                            th: MarkedTh,
                            td: MarkedTd,
                            // The model's citations point off-site; they open in
                            // a new tab so a reviewer does not lose the panel,
                            // and carry noopener because the destination is not
                            // ours.
                            a: ({ href, children }) => (
                                <a href={href} target="_blank" rel="noopener noreferrer">
                                    {children}
                                </a>
                            ),
                            h1: AnswerHeading,
                            h2: AnswerHeading,
                            h3: AnswerHeading,
                            h4: AnswerHeading,
                            h5: AnswerHeading,
                            h6: AnswerHeading,
                            // Each element marks the text directly inside it. A
                            // bold name is an element within a paragraph, so
                            // `strong` marks its own text too.
                            p: MarkedP,
                            li: MarkedLi,
                            strong: MarkedStrong,
                            em: MarkedEm,
                        }}
                    >
                        {body}
                    </ReactMarkdown>
                </div>
                {!expanded && overflows && (
                    // Fades into the cut rather than ending mid-sentence at a
                    // hard edge, so it reads as "there is more" instead of
                    // "this is broken".
                    <div className={cn("pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white to-transparent", reading ? "h-20" : "h-8")} />
                )}
            </div>
            {clampable && overflows && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className={cn(
                        "mt-2 font-medium",
                        reading
                            ? "inline-flex h-8 items-center rounded-full bg-gray-100 px-3.5 text-[13px] text-gray-900 hover:bg-gray-200"
                            : "text-[13px] leading-[18px] text-blue-600 hover:underline",
                    )}
                >
                    {expanded ? "Show less" : reading ? "Read the full answer" : "Show full answer"}
                </button>
            )}
        </div>
        </HighlightContext.Provider>
    );
}
